import Box from '@mui/material/Box/Box';
import './Organization.scss';
import { useQuery } from '@tanstack/react-query';
import { Loader, Select, Tab, Tabs } from '@trackman/web-shared-components';
import { OrganizationTabsRelativeRoutes } from 'Routes';
import i18next from 'i18next';
import { useServices, useStores } from 'index';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import { useOrganizationStore } from 'stores/Organization/OrganizationStore';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useTabsStore } from 'stores/TabsStore';

type TabRouteKeys = keyof typeof OrganizationTabsRelativeRoutes;

const organizationTabNamesByRoutes: { [key in TabRouteKeys]: string } = {
  Progress: i18next.t('StudentsProgress', { ns: 'general' }),
  Students: i18next.t('Students', { ns: 'general' }),
  Classes: i18next.t('Classes', { ns: 'studentsPage' }),
} as const;

export const Organization = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const cs = useStores().classesStore;
  useSetPageTitle({
    title: t('Organization', { ns: 'general' }),
  });

  const organizationsService = useServices().organizationsService;
  const { data: organizationList, isPending: isOrganizationListPending } = useQuery({
    queryKey: ['organizationList'],
    queryFn: organizationsService.getOrganizations,
    refetchOnWindowFocus: false,
  });

  const { activeTabIndex } = useTabsStore();

  const setSelectedOrganizationId = useOrganizationStore((state) => state.setSelectedOrganizationId);
  const selectedOrganizationId = useOrganizationStore((state) => state.selectedOrganizationId);

  useEffect(() => {
    if (organizationList && organizationList.length > 0) {
      setSelectedOrganizationId(organizationList[0].id);
    }
  }, [organizationList]);

  useEffect(() => {
    selectedOrganizationId && cs.getClasses(false, selectedOrganizationId);
  }, [selectedOrganizationId]);

  return (
    <>
      {!isOrganizationListPending ? (
        <>
          <div className='default-page-header'>
            <h1 className='page-title'>{t('Organization', { ns: 'general' })}</h1>
            <span className='page-subtitle'>{t('TrackProgressAndCourseCompletion', { ns: 'organizationPage' })}</span>
          </div>
          <div className='select-organization-section'>
            <h2>{t('Organization', { ns: 'general' })}</h2>
            <span>{t('PleaseSelectTheOrganizationYouArePlanningToAdministrate', { ns: 'organizationPage' })}</span>
            <Select
              label={t('Organization', { ns: 'general' })}
              value={selectedOrganizationId}
              error={false}
              onChange={(event) => {
                const selectedOrg = organizationList.find((organization) => organization.id === (event.target.value as string));
                if (selectedOrg) {
                  setSelectedOrganizationId(selectedOrg.id);
                }
              }}
              selectOptions={organizationList.map((organization) => {
                return {
                  label: organization.name === ' ' ? t('PleaseSelectOrganization', { ns: 'organizationPage' }) : organization.name,
                  value: organization.id,
                };
              })}
            />
          </div>
          <Box className='tabs-container'>
            <Tabs
              scrollButtons='auto'
              value={activeTabIndex}
              variant='scrollable'
              indicatorColor='primary'
              allowScrollButtonsMobile
            >
              <Tab label={t('StudentsProgress', { ns: 'general' })} onClick={() => navigate(OrganizationTabsRelativeRoutes.Progress)} />
              <Tab label={t('Students', { ns: 'general' })} onClick={() => navigate(OrganizationTabsRelativeRoutes.Students)} />
              <Tab label={t('Classes', { ns: 'studentsPage' })} onClick={() => navigate(OrganizationTabsRelativeRoutes.Classes)} />
            </Tabs>
          </Box>
          <Outlet />
        </>
      ) : (
        <div className='loader-wrapper'>
          <Loader size='large' />
        </div>
      )}
    </>
  );
};
